'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { GuideEyebrow, GuideSection, GuideSubtitle, GuideTitle } from '../../../components/GuideHeading';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

export default function JsonPatchVsMergePatchGuide() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" className="main-container">
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        <GuideEyebrow>Guide</GuideEyebrow>
                        <GuideTitle>JSON Patch vs JSON Merge Patch</GuideTitle>
                        <GuideSubtitle>
                            RFC 6902 and RFC 7396 both describe how to update a JSON document with an HTTP PATCH request, but they work very differently. Here is how to choose.
                        </GuideSubtitle>

                        <GuideSection>The one-minute version</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            <strong>JSON Patch (RFC 6902)</strong> is a list of explicit operations - add, remove, replace, move, copy, test - applied in order. <strong>JSON Merge Patch (RFC 7396)</strong> is a partial copy of the target document: keys you send replace the old values, and <code>null</code> deletes a key. Merge Patch is simpler to write by hand; JSON Patch is more precise and the only one of the two that can target individual array elements.
                        </Typography>

                        <GuideSection>Side by side</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Same change - bump a version, add a tag, delete a field - in both formats:
                        </Typography>
                        <Code>{`// Original document
{ "name": "demo", "version": 1, "tags": ["a", "b"] }

// JSON Patch (RFC 6902) - media type application/json-patch+json
[
  { "op": "replace", "path": "/version", "value": 2 },
  { "op": "add", "path": "/tags/-", "value": "c" },
  { "op": "remove", "path": "/name" }
]

// JSON Merge Patch (RFC 7396) - media type application/merge-patch+json
{ "version": 2, "tags": ["a", "b", "c"], "name": null }`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            Both produce <code>{'{ "version": 2, "tags": ["a", "b", "c"] }'}</code> - but look at what Merge Patch needed for the array: the entire new array. JSON Patch appended one element; Merge Patch had to replace the whole value.
                        </Typography>

                        <GuideSection>Where JSON Patch wins</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Array surgery.</strong> Insert, remove, or reorder individual array elements without resending the whole array. Merge Patch can only replace arrays wholesale.</li>
                            <li><strong>Optimistic concurrency.</strong> The test operation fails the patch if the document is not in the expected state - a built-in conflict guard.</li>
                            <li><strong>Ordered evaluation.</strong> Operations run in sequence and stop at the first error; rollback depends on the implementation.</li>
                            <li><strong>Auditability.</strong> An operation list reads like a changelog, which is why tools like Kubernetes use it.</li>
                        </Typography>

                        <GuideSection>Where Merge Patch wins</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Simplicity.</strong> If you can write the resource, you can write the patch - it is just a partial document. Great for flat objects and hand-written API calls.</li>
                            <li><strong>Small payloads for shallow updates.</strong> Changing three top-level fields takes three key-value pairs instead of three operation objects.</li>
                        </Typography>

                        <GuideSection>The null trap</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Merge Patch deletes keys with <code>null</code> - which means you cannot set a key to an actual JSON null. If your data model uses null as a meaningful value, Merge Patch cannot express it. JSON Patch distinguishes <code>replace</code> with a null value from <code>remove</code> cleanly:
                        </Typography>
                        <Code>{`[
  { "op": "replace", "path": "/middleName", "value": null },
  { "op": "remove", "path": "/nickname" }
]`}</Code>

                        <GuideSection>Which should your API use?</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            For most resource APIs, supporting both is common: accept <code>application/merge-patch+json</code> for convenience and <code>application/json-patch+json</code> for precision. If you can only pick one, choose JSON Patch when arrays, concurrency control, or meaningful nulls matter - and Merge Patch when your documents are flat and your clients write patches by hand.
                        </Typography>

                        <GuideSection>See the difference yourself</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Paste two documents into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> and look at the RFC 6902 operations it produces - then imagine expressing the same change as a Merge Patch. The{' '}
                            <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link> shows the raw line-level changes underneath both formats.
                        </Typography>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 4 }}>
                            Go deeper: <Link href="/docs" style={{ color: 'var(--accent)' }}>JSON Patch documentation</Link>,{' '}
                            <Link href="/examples" style={{ color: 'var(--accent)' }}>examples</Link>, and the{' '}
                            <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link> that makes JSON Patch safe for concurrent updates.
                        </Typography>

                        <RelatedPages currentPage="docs" />
                    </Box>
                </Container>
            </main>
        </>
    );
}
